import {
  SBSelectRaw,
  SBState,
  baseReducers,
  getIdOrModelId,
} from '../utils/helpers/ReducerHelper'
import {
  SBAPICreateDispatch,
  SBAPIDeleteDispatch,
  SBAPIFetchDispatch,
  SBAPIFetchPaginatedDispatch,
  SBAPIUpdateDispatch,
} from '../utils/helpers/SBAPIHelper'
import { ALERT_URL } from '../utils/urls'
import { Alert } from '../models/Alert'
import { AlertCreate } from '../models/AlertCtreate'
import { AppDispatch, RootState } from './store'
import { alertSchema } from '../models/schema'
import { initialQuery } from '../utils/helpers/CRUDHelper'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import { TableParams } from '../models/TableParams'

const initialState: SBState<Alert> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'alert',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer

export const {
  getItemsSuccess: getAlertsSuccess,
  setQuery: setAlertQuery,
  resetQueryAndIds: resetAlertQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<{ [key: string]: Alert }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectAlerts = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectAllAlerts = () =>
  createSelector([selectRawItems], (items) =>
    Object.entries(items).map((i) => i[1])
  )
export const selectSelectedAlert = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectAlertById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectAlertsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedAlert =
  (Alert: Alert | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<Alert>(Alert)))

export const getAlerts = (params: TableParams) =>
  SBAPIFetchPaginatedDispatch<Alert>(
    ALERT_URL,
    params,
    [alertSchema],
    slice.actions
  )

export const getAlertWithId = (id: number) =>
  SBAPIFetchDispatch<Alert>(`${ALERT_URL}/${id}`, alertSchema, slice.actions)

export const createAlert = (alert: AlertCreate) =>
  SBAPICreateDispatch<AlertCreate>(alert, ALERT_URL, alertSchema, slice.actions)

export const updateAlert = (alert: Alert) =>
  SBAPIUpdateDispatch<Alert>(alert, `${ALERT_URL}/${alert.id}`, slice.actions)

export const deleteAlert = (alert: Alert) =>
  SBAPIDeleteDispatch<Alert>(alert, `${ALERT_URL}/${alert.id}`, slice.actions)
